import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import uiConfig from "../uiConfig";
import { useAuth } from "../contexts/AuthContext";
import { fetchResolveEventBySlug } from "../shared/api/resolveEventBySlug";
import { getCurrentSubdomain } from "../shared/utils/subdomain";
import { EVENT_PLACEHOLDER_REGISTRATION_MESSAGE } from "../shared/utils/eventPlaceholder";
import { buildEventCalendarPath, buildEventCheckInPath } from "../shared/utils/eventCalendarNavigation";

const LOADING_WRAP = {
  padding: "48px 24px",
  textAlign: "center",
  color: "#6c757d",
  fontSize: "0.95rem",
};

/**
 * Resolves `/e/{slug}` without mounting Event Calendar.
 * Public events → calendar registration deep link.
 * Private events → staff Event Check-In (login first when needed).
 */
function EventVanityLinkRedirect() {
  const { eventSlug: eventSlugParam } = useParams();
  const [searchParams] = useSearchParams();
  const { user, userInfo, loading: authLoading } = useAuth();
  const [resolveState, setResolveState] = useState({ status: "loading", data: null, error: "" });

  const slug = eventSlugParam ? String(eventSlugParam).trim().toLowerCase() : "";
  const subdomain = getCurrentSubdomain();
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const publicCalendarAccountId = uiConfig.publicCalendarAccountId || null;
  const accountIdFromQuery = (searchParams.get("account_id") || "").trim();

  useEffect(() => {
    if (!slug) {
      setResolveState({ status: "error", data: null, error: "Invalid event link." });
      return undefined;
    }

    const siteName = subdomain || "";
    const accountId = accountIdFromQuery || userInfo?.account_id || publicCalendarAccountId || "";
    if (!siteName && !accountId) {
      setResolveState({
        status: "error",
        data: null,
        error:
          "This event link needs an organization. Open it from your organization's site or add account_id.",
      });
      return undefined;
    }

    let cancelled = false;
    setResolveState({ status: "loading", data: null, error: "" });

    fetchResolveEventBySlug({
      serverUrl,
      slug,
      siteName,
      accountId: siteName ? "" : accountId,
    })
      .then((data) => {
        if (cancelled) return;
        if (data.is_placeholder) {
          setResolveState({
            status: "error",
            data: null,
            error: EVENT_PLACEHOLDER_REGISTRATION_MESSAGE,
          });
          return;
        }
        if (String(data.status || "").toLowerCase() === "closed") {
          setResolveState({
            status: "error",
            data: null,
            error: "This event is closed.",
          });
          return;
        }
        setResolveState({ status: "success", data, error: "" });
      })
      .catch((err) => {
        if (!cancelled) {
          setResolveState({
            status: "error",
            data: null,
            error: err && err.message ? err.message : "Could not load this event link.",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug, subdomain, accountIdFromQuery, userInfo?.account_id, publicCalendarAccountId, serverUrl]);

  const redirectTo = useMemo(() => {
    if (resolveState.status !== "success" || !resolveState.data) return null;
    const { eventId, accountId, visibility } = resolveState.data;

    if (visibility === "private") {
      if (authLoading) return null;
      const checkInPath = buildEventCheckInPath({ eventId, accountId });
      if (!user) {
        return `/Login?returnUrl=${encodeURIComponent(checkInPath)}`;
      }
      return checkInPath;
    }

    return buildEventCalendarPath({ accountId, attendEventId: eventId });
  }, [resolveState, authLoading, user]);

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (resolveState.status === "error") {
    return (
      <div style={LOADING_WRAP}>
        <p style={{ color: "#842029", margin: 0 }}>{resolveState.error}</p>
      </div>
    );
  }

  const loadingMessage =
    resolveState.status === "success" && resolveState.data?.visibility === "private" && authLoading
      ? "Opening check-in…"
      : "Loading event…";

  return <div style={LOADING_WRAP}>{loadingMessage}</div>;
}

export default EventVanityLinkRedirect;
